import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import { fetchPage } from '@vangst/services/oogst/page'
import {
  ElasticSearchEndpoints,
  ElasticSearchVars,
  prefetchElasticSearch,
} from '@vangst/services/oogst/search/useElasticSearch'
import { RobotsEnum } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import Busy from '../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../components/navigation/Breadcrumbs'
import ClickyExplore from '../../../../components/navigation/ClickyExplore'
import Markdown from '../../../../components/views/Markdown'
import PageHeader from '../../../../components/views/PageHeader'
import Meta from '../../../../domains/application/Meta'
import SearchRenderer from '../../../../domains/search/SearchRenderer'

type StaticProps = GetStaticPropsContext<{ country: string }>

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

type SearchType = {
  title: string
  endpoint: ElasticSearchEndpoints
  variables: ElasticSearchVars
}

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const country = params?.country
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[country]', country || '')

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.JOBS, text: 'Jobs' },
    { href: Routes.JOBS_BROWSE, text: 'Browse Cannabis Jobs' },
    { href: null, text: country?.toUpperCase() ?? 'US' },
  ]

  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = {
    ...getPage,
    title: getPage?.title ?? 'Employment, Cannabis Jobs in the US',
    subtitle:
      getPage?.subtitle ??
      'Looking for cannabis jobs near you? Vangst offers opportunities in the cannabis industry across the United States. Explore diverse job openings including budtender jobs, grower jobs, cultivation jobs, dispensary and dispensary managers jobs, and more.',
  }

  const search: SearchType = {
    title: 'Current Cannabis Jobs in the US',
    endpoint: 'searchJobPostings',
    variables: { first: 12 },
  }
  const es = await prefetchElasticSearch(
    queryClient,
    search.endpoint,
    search.variables,
  )
  const hasResults = es?.nodes?.length

  return {
    props: {
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      crumbs,
      hasResults,
      page,
      search,
    },
    notFound: country !== 'us',
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function Page(props: PropsType) {
  const { crumbs, hasResults, page, search } = props

  if (!page || !search)
    return (
      <article className="contain-xyc mood-white">
        <Busy />
      </article>
    )

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="mood-white">
        <PageHeader
          icon="pin drop"
          title={page.title}
          subtitle={page.subtitle}
          description={page.description}
          imgAlt={page.pagePhotos?.[0]?.alt}
          imgSrc={page.pagePhotos?.[0]?.url}
        />
        <div className="contain-xyc flow-y-sm">
          <h2 className="heading-md">{search.title}</h2>
          {hasResults ? (
            <SearchRenderer
              as="card"
              at="infinite-preview"
              classNameList="grid-three-up min-h-64"
              endpoint={search.endpoint}
              variables={search.variables}
            />
          ) : (
            <ClickyExplore className="mt-auto" href={Routes.JOBS}>
              View All Cannabis Jobs
            </ClickyExplore>
          )}
        </div>

        {page.markdownContent && (
          <section className="contain-xyc mood-white">
            <div className="text-sm">
              <Markdown renderType="basic">{page.markdownContent}</Markdown>
            </div>
          </section>
        )}
      </article>
    </>
  )
}
